<template>
    <section :class="['analytics-program-dashboard', { dark: isDark }]">
        <header class="program-header">
            <div>
                <p class="eyebrow">Analytics program</p>
                <h1>Operational dashboards first. Governance stays visible.</h1>
                <p class="subtitle">
                    Six in-store dashboards are live. This page separates current decision support from the source, definition,
                    and ownership work still required—without blocking day-to-day use.
                </p>
            </div>
            <router-link class="header-link" to="/dashboard/sales-revenue">
                Open Sales &amp; Revenue
                <i class="material-icons" aria-hidden="true">arrow_forward</i>
            </router-link>
        </header>

        <section class="program-summary" aria-label="Analytics program summary">
            <div><strong>{{ liveCount }}</strong><span>Live</span></div>
            <div><strong>{{ limitedCount }}</strong><span>Live with limitations</span></div>
            <div><strong>{{ deferredCount }}</strong><span>Deferred</span></div>
            <p>Active completion work excludes Ecommerce.</p>
        </section>

        <div class="program-layout">
            <main class="program-surface">
                <div class="section-heading">
                    <div>
                        <p class="eyebrow">Active scope</p>
                        <h2>Non-ecommerce dashboard closeout</h2>
                    </div>
                    <span>{{ activeGoals.length }} dashboards</span>
                </div>

                <article v-for="goal in activeGoals" :key="goal.id" class="program-row">
                    <div class="goal-heading">
                        <div>
                            <h3>{{ goal.title }}</h3>
                            <span :class="['status-pill', goal.status]">{{ formatStatus(goal.status) }}</span>
                        </div>
                        <router-link :to="goal.route">Open dashboard <i class="material-icons" aria-hidden="true">arrow_forward</i></router-link>
                    </div>

                    <div class="goal-columns">
                        <div>
                            <h4>Live now</h4>
                            <ul class="live-list">
                                <li v-for="item in goal.liveMetrics" :key="item">
                                    <i class="material-icons" aria-hidden="true">check_circle</i>
                                    <span>{{ item }}</span>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h4>{{ goal.status === 'live' ? 'Follow-up' : 'Completion work' }}</h4>
                            <ol class="remaining-list">
                                <li v-for="item in goal.remainingWork" :key="item">{{ item }}</li>
                            </ol>
                        </div>
                    </div>
                </article>
            </main>

            <aside class="governance-panel">
                <p class="eyebrow">Shared foundation</p>
                <h2>{{ setupGoal.title }}</h2>
                <p>{{ setupGoal.description }}</p>

                <ol class="governance-list">
                    <li v-for="(item, index) in governanceItems" :key="item.title">
                        <span>{{ index + 1 }}</span>
                        <div><strong>{{ item.title }}</strong><p>{{ item.description }}</p></div>
                    </li>
                </ol>

                <div class="definition-box">
                    <strong>Definition of complete</strong>
                    <p>A metric has an approved formula, source, owner, refresh expectation, permission boundary, and reconciliation path.</p>
                </div>
            </aside>
        </div>

        <section v-if="deferredGoal" class="deferred-section">
            <div>
                <p class="eyebrow">Deferred by decision</p>
                <h2>{{ deferredGoal.title }}</h2>
                <p>{{ deferredGoal.remainingWork[0] }}</p>
            </div>
            <span class="status-pill deferred">Deferred</span>
        </section>
    </section>
</template>

<script>
import { mapActions, mapState } from "vuex"
import {
    ANALYTICS_DASHBOARD_GOALS,
    DATA_ANALYTICS_SETUP_GOAL,
} from "../../utils/analytics-dashboard-goals"

const STATUS_LABELS = Object.freeze({
    live: "Live",
    live_with_limitations: "Live with limitations",
    deferred: "Deferred",
})

export default {
    name: "AnalyticsProgramDashboard",
    data() {
        return {
            goals: ANALYTICS_DASHBOARD_GOALS,
            setupGoal: DATA_ANALYTICS_SETUP_GOAL,
            governanceItems: [
                {
                    title: "Reporting source contract",
                    description: "Document the production database as the current reporting source and define when a replica or warehouse becomes necessary.",
                },
                {
                    title: "Metric catalog",
                    description: "Approve formulas, Toronto business dates, pilot-sale rules, refund treatment, customer identity, and inventory thresholds.",
                },
                {
                    title: "Refresh and quality ownership",
                    description: "Assign performance, freshness, reconciliation, and data-quality follow-up to named operational owners.",
                },
            ],
        }
    },
    computed: {
        ...mapState("misc", ["theme", "THEMES"]),
        isDark() { return this.theme === this.THEMES.DARK },
        activeGoals() { return this.goals.filter(goal => goal.status !== "deferred") },
        deferredGoal() { return this.goals.find(goal => goal.status === "deferred") },
        liveCount() { return this.goals.filter(goal => goal.status === "live").length },
        limitedCount() { return this.goals.filter(goal => goal.status === "live_with_limitations").length },
        deferredCount() { return this.goals.filter(goal => goal.status === "deferred").length },
    },
    mounted() {
        this.setData({ pageTitle: "Analytics Program" })
    },
    methods: {
        ...mapActions("misc", ["setData"]),
        formatStatus(status) { return STATUS_LABELS[status] || String(status || "").replace(/_/g, " ") },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.analytics-program-dashboard {
    color: $textColor;
    margin: 0 auto;
    max-width: 1480px;
    padding: 28px;

    &.dark { color: $darkTextColor; }
}

.program-header {
    align-items: flex-start;
    display: flex;
    gap: 28px;
    justify-content: space-between;
    margin-bottom: 22px;

    h1 {
        color: $primaryColor;
        font-size: clamp(26px, 3vw, 40px);
        letter-spacing: -0.035em;
        line-height: 1.08;
        margin: 0;
        max-width: 760px;
    }
}

.dark .program-header h1 { color: $secondaryColorDark; }

.eyebrow {
    color: rgba($textColor, 0.54);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.06em;
    margin: 0 0 6px;
    text-transform: uppercase;
}

.dark .eyebrow { color: rgba($darkTextColor, 0.58); }

.subtitle {
    color: rgba($textColor, 0.68);
    line-height: 1.6;
    margin: 12px 0 0;
    max-width: 760px;
}

.dark .subtitle { color: rgba($darkTextColor, 0.7); }

.header-link,
.goal-heading a {
    align-items: center;
    color: $primaryColor;
    display: inline-flex;
    font-size: 12px;
    font-weight: 700;
    gap: 5px;
    text-decoration: none;
    white-space: nowrap;

    i { font-size: 17px; }
    &:hover { text-decoration: underline; }
    &:active { transform: translateY(1px); }
}

.dark .header-link,
.dark .goal-heading a { color: $secondaryColorDark; }

.program-summary {
    align-items: center;
    border-bottom: 1px solid $lightBorderColor;
    border-top: 1px solid $lightBorderColor;
    display: flex;
    gap: 34px;
    margin-bottom: 26px;
    padding: 15px 0;

    div { align-items: baseline; display: flex; gap: 7px; }
    strong { color: $primaryColor; font-size: 24px; }
    span, p { color: rgba($textColor, 0.62); font-size: 12px; }
    p { margin: 0 0 0 auto; }
}

.dark .program-summary {
    border-color: $darkBorderColor;
    strong { color: $secondaryColorDark; }
    span, p { color: rgba($darkTextColor, 0.66); }
}

.program-layout {
    align-items: start;
    display: grid;
    gap: 24px;
    grid-template-columns: minmax(0, 1fr) minmax(280px, 0.34fr);
}

.program-surface,
.governance-panel {
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 10px;
}

.dark .program-surface,
.dark .governance-panel {
    background: $darkCardBg;
    border-color: $darkBorderColor;
}

.section-heading {
    align-items: center;
    border-bottom: 1px solid $lightBorderColor;
    display: flex;
    justify-content: space-between;
    padding: 20px 22px;

    h2 { color: $primaryColor; font-size: 18px; margin: 0; }
    > span { color: rgba($textColor, 0.55); font-size: 11px; }
}

.dark .section-heading {
    border-color: $darkBorderColor;
    h2 { color: $secondaryColorDark; }
    > span { color: rgba($darkTextColor, 0.58); }
}

.program-row { padding: 22px; }
.program-row + .program-row { border-top: 1px solid $lightBorderColor; }
.dark .program-row + .program-row { border-color: $darkBorderColor; }

.goal-heading {
    align-items: center;
    display: flex;
    gap: 16px;
    justify-content: space-between;

    > div { align-items: center; display: flex; gap: 9px; }
    h3 { color: $primaryColor; font-size: 17px; margin: 0; }
}

.dark .goal-heading h3 { color: $secondaryColorDark; }

.goal-columns {
    display: grid;
    gap: 30px;
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
    margin-top: 17px;

    h4 {
        color: rgba($textColor, 0.58);
        font-size: 10px;
        letter-spacing: 0.05em;
        margin: 0 0 10px;
        text-transform: uppercase;
    }
}

.dark .goal-columns h4 { color: rgba($darkTextColor, 0.6); }

.live-list,
.remaining-list { display: grid; gap: 8px; margin: 0; padding: 0; }
.live-list { list-style: none; }
.live-list li { align-items: flex-start; display: flex; gap: 7px; }
.live-list i { color: $successColor; font-size: 16px; margin-top: 1px; }
.live-list span,
.remaining-list li { color: rgba($textColor, 0.7); font-size: 12px; line-height: 1.45; }
.remaining-list { padding-left: 18px; }
.dark .live-list span,
.dark .remaining-list li { color: rgba($darkTextColor, 0.72); }
.dark .live-list i { color: $successColorDark; }

.status-pill {
    border-radius: 999px;
    font-size: 9px;
    font-weight: 800;
    padding: 4px 7px;
    text-transform: uppercase;
    white-space: nowrap;

    &.live { background: rgba($successColor, 0.13); color: darken($successColor, 16%); }
    &.live_with_limitations { background: rgba($warningColor, 0.13); color: darken($warningColor, 24%); }
    &.deferred { background: rgba($textColor, 0.08); color: rgba($textColor, 0.62); }
}

.dark .status-pill {
    &.live { background: rgba($successColorDark, 0.15); color: $successColorDark; }
    &.live_with_limitations { background: rgba($warningColorDark, 0.15); color: $warningColorDark; }
    &.deferred { background: rgba($darkTextColor, 0.1); color: rgba($darkTextColor, 0.66); }
}

.governance-panel {
    padding: 22px;
    position: sticky;
    top: 18px;

    h2 { color: $primaryColor; font-size: 19px; margin: 0; }
    > p:not(.eyebrow) { color: rgba($textColor, 0.68); font-size: 12px; line-height: 1.55; }
}

.dark .governance-panel {
    h2 { color: $secondaryColorDark; }
    > p:not(.eyebrow) { color: rgba($darkTextColor, 0.7); }
}

.governance-list { display: grid; gap: 16px; list-style: none; margin: 20px 0; padding: 0; }
.governance-list li { display: grid; gap: 10px; grid-template-columns: 28px minmax(0, 1fr); }
.governance-list li > span { align-items: center; background: rgba($primaryColor, 0.08); border-radius: 50%; color: $primaryColor; display: flex; font-size: 11px; font-weight: 800; height: 28px; justify-content: center; }
.governance-list strong { color: $primaryColor; font-size: 12px; }
.governance-list p { color: rgba($textColor, 0.66); font-size: 11px; line-height: 1.5; margin: 4px 0 0; }
.dark .governance-list li > span { background: rgba($secondaryColorDark, 0.12); color: $secondaryColorDark; }
.dark .governance-list strong { color: $secondaryColorDark; }
.dark .governance-list p { color: rgba($darkTextColor, 0.68); }

.definition-box { background: rgba($warningColor, 0.07); border-left: 3px solid $warningColor; padding: 13px; }
.definition-box strong { color: $primaryColor; font-size: 12px; }
.definition-box p { color: rgba($textColor, 0.68); font-size: 11px; line-height: 1.5; margin: 5px 0 0; }
.dark .definition-box { background: rgba($warningColorDark, 0.08); border-color: $warningColorDark; }
.dark .definition-box strong { color: $secondaryColorDark; }
.dark .definition-box p { color: rgba($darkTextColor, 0.7); }

.deferred-section {
    align-items: center;
    border-top: 1px solid $lightBorderColor;
    display: flex;
    justify-content: space-between;
    margin-top: 26px;
    padding: 20px 2px 0;

    h2 { color: $primaryColor; font-size: 18px; margin: 0; }
    p:not(.eyebrow) { color: rgba($textColor, 0.64); font-size: 12px; margin: 5px 0 0; }
}

.dark .deferred-section {
    border-color: $darkBorderColor;
    h2 { color: $secondaryColorDark; }
    p:not(.eyebrow) { color: rgba($darkTextColor, 0.66); }
}

@media (max-width: 980px) {
    .program-layout { grid-template-columns: 1fr; }
    .governance-panel { position: static; }
}

@media (max-width: 680px) {
    .analytics-program-dashboard { padding: 14px; }
    .program-header { flex-direction: column; }
    .program-summary { align-items: flex-start; flex-wrap: wrap; gap: 14px 24px; }
    .program-summary p { flex-basis: 100%; margin: 0; }
    .section-heading { align-items: flex-start; flex-direction: column; gap: 6px; }
    .goal-heading { align-items: flex-start; flex-direction: column; }
    .goal-columns { grid-template-columns: 1fr; }
    .deferred-section { align-items: flex-start; gap: 14px; }
}
</style>
